/**
 * Data Access Layer for Tags
 *
 * A generic, account-scoped label catalog applied to happenings, plus the
 * happening ↔ tag join. Same shape as the staff role catalog in staff-roles.ts.
 *
 * Tags are descriptive and applied by hand: there is no fulfilment state and no
 * automatic assignment. See the migration comment in
 * supabase/migrations/20260819000001_happening_tags.sql for why.
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import { getCurrentAccountId } from './account-context.js';
import type { Tag, TagCreate, TagUpdate } from './types.js';

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

// ============================================
// Tag catalog
// ============================================

export async function getTag(id: string): Promise<Tag | null> {
  const { data, error } = await getClient()
    .from(TABLES.tags)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as Tag;
}

export async function listTags(): Promise<Tag[]> {
  let query = getClient().from(TABLES.tags).select('*');

  const accountId = getCurrentAccountId();
  if (accountId) query = query.eq('account_id', accountId);

  const { data, error } = await query.order('label');
  if (error) throw error;
  return (data || []) as Tag[];
}

export async function createTag(input: TagCreate): Promise<Tag> {
  const accountId = getCurrentAccountId();
  if (!accountId) throw new Error('createTag: no account context set');

  const { data, error } = await getClient()
    .from(TABLES.tags)
    .insert({
      account_id: accountId,
      label: input.label,
      slug: input.slug?.trim() || slugify(input.label),
      ...(input.color !== undefined ? { color: input.color } : {}),
      ...(input.description !== undefined ? { description: input.description } : {}),
    })
    .select()
    .single();

  if (error) throw error;
  return data as Tag;
}

export async function updateTag(id: string, input: TagUpdate): Promise<Tag> {
  const patch: Record<string, unknown> = {};
  if (input.label !== undefined) patch.label = input.label;
  if (input.slug !== undefined) patch.slug = slugify(input.slug);
  if (input.color !== undefined) patch.color = input.color;
  if (input.description !== undefined) patch.description = input.description;

  const { data, error } = await getClient()
    .from(TABLES.tags)
    .update(patch)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Tag;
}

/** Deleting a tag removes it from every happening that carried it (ON DELETE CASCADE). */
export async function deleteTag(id: string): Promise<void> {
  const { error } = await getClient().from(TABLES.tags).delete().eq('id', id);
  if (error) throw error;
}

// ============================================
// Happening ↔ tag assignment
// ============================================

export async function listHappeningTags(happeningId: string): Promise<Tag[]> {
  const { data, error } = await getClient()
    .from(TABLES.happening_tags)
    .select('tag:tag_id (*)')
    .eq('happening_id', happeningId);

  if (error) throw error;
  return (data || [])
    .map((row: any) => row.tag)
    .filter(Boolean) as Tag[];
}

/**
 * Replace a happening's full set of tags in one go.
 *
 * Delete-then-insert rather than a diff: the set is small, and the join row carries
 * nothing worth preserving (no state, no author), so a recreated row loses nothing
 * but its created_at.
 */
export async function setHappeningTags(happeningId: string, tagIds: string[]): Promise<void> {
  const client = getClient();

  const { error: deleteError } = await client
    .from(TABLES.happening_tags)
    .delete()
    .eq('happening_id', happeningId);
  if (deleteError) throw deleteError;

  if (tagIds.length > 0) {
    const rows = tagIds.map((tag_id) => ({ happening_id: happeningId, tag_id }));
    const { error: insertError } = await client.from(TABLES.happening_tags).insert(rows);
    if (insertError) throw insertError;
  }
}

/** How many happenings currently carry each tag, keyed by tag id. For the manage-tags UI. */
export async function countHappeningsPerTag(): Promise<Record<string, number>> {
  const { data, error } = await getClient()
    .from(TABLES.happening_tags)
    .select('tag_id');

  if (error) throw error;

  const counts: Record<string, number> = {};
  for (const row of (data || []) as { tag_id: string }[]) {
    counts[row.tag_id] = (counts[row.tag_id] ?? 0) + 1;
  }
  return counts;
}
